import { isValidDate } from "../../../../utils/isValidDate";
import {
  AddressDto,
  AssetDocumentArea,
  AssetDocumentZone,
  AssetPropertyRightKind,
  PropertyRightAnswers,
  PropertyRightsTab,
  SavePropertyRights,
} from "../../../api/ClientFileApi";
import { ClientFileQueryKey } from "../../../query/clientFileQueryKeys";
import { InventoryUploadAreaConfig } from "../../components/inventory-upload-area/InventoryUploadArea";
import {
  PropertyRightAnswerField,
  PropertyRightDocumentField,
  PropertyRightFormValue,
  PropertyRightsFormValues,
} from "./OtherPropertyRightsView.types";

export type PropertyRightTranslationKey = "tenancy" | "companyShares" | "cooperativeMemberships" | "otherRights";

export interface PropertyRightKindConfig extends InventoryUploadAreaConfig<PropertyRightDocumentField> {
  kind: AssetPropertyRightKind;
  translationKey: PropertyRightTranslationKey;
  entryField: PropertyRightAnswerField;
}

const PROPERTY_RIGHT_KIND_CONFIGS: Record<AssetPropertyRightKind, PropertyRightKindConfig> = {
  [AssetPropertyRightKind.Tenancy]: {
    kind: AssetPropertyRightKind.Tenancy,
    translationKey: "tenancy",
    entryField: "hasTenancyRights",
    area: AssetDocumentArea.TenancyRights,
    zone: AssetDocumentZone.TenancyRights,
    queryKey: ClientFileQueryKey.AssetInventoryTenancyRightDocuments,
    fieldName: "tenancyRightDocuments",
  },
  [AssetPropertyRightKind.CompanyShare]: {
    kind: AssetPropertyRightKind.CompanyShare,
    translationKey: "companyShares",
    entryField: "hasCompanyShares",
    area: AssetDocumentArea.CompanyShares,
    zone: AssetDocumentZone.CompanyShares,
    queryKey: ClientFileQueryKey.AssetInventoryCompanyShareDocuments,
    fieldName: "companyShareDocuments",
  },
  [AssetPropertyRightKind.CooperativeMembership]: {
    kind: AssetPropertyRightKind.CooperativeMembership,
    translationKey: "cooperativeMemberships",
    entryField: "hasCooperativeMemberships",
    area: AssetDocumentArea.CooperativeMemberships,
    zone: AssetDocumentZone.CooperativeMemberships,
    queryKey: ClientFileQueryKey.AssetInventoryCooperativeMembershipDocuments,
    fieldName: "cooperativeMembershipDocuments",
  },
  [AssetPropertyRightKind.Other]: {
    kind: AssetPropertyRightKind.Other,
    translationKey: "otherRights",
    entryField: "hasOtherPropertyRights",
    area: AssetDocumentArea.OtherPropertyRights,
    zone: AssetDocumentZone.OtherPropertyRights,
    queryKey: ClientFileQueryKey.AssetInventoryOtherPropertyRightDocuments,
    fieldName: "otherPropertyRightDocuments",
  },
};

export const PROPERTY_RIGHT_KINDS: readonly PropertyRightKindConfig[] = Object.values(PROPERTY_RIGHT_KIND_CONFIGS);

export interface DetailRightKindConfig extends PropertyRightKindConfig {
  translationKey: Exclude<PropertyRightTranslationKey, "tenancy">;
}

export const DETAIL_RIGHT_KINDS: readonly DetailRightKindConfig[] = PROPERTY_RIGHT_KINDS.filter(
  (config): config is DetailRightKindConfig => config.translationKey !== "tenancy"
);

export function kindConfigOf(kind: AssetPropertyRightKind): PropertyRightKindConfig {
  return PROPERTY_RIGHT_KIND_CONFIGS[kind];
}

/** Whether the entry question of the kind is answered with Ja, which shows its blocks and its upload area. */
export function hasRightsOf(answers: PropertyRightAnswers, kind: AssetPropertyRightKind): boolean {
  return answers[kindConfigOf(kind).entryField] === true;
}

export function formatHomeAddress({ street, houseNumber, zipCode, city }: AddressDto): string {
  const streetPart = [street, houseNumber].filter(Boolean).join(" ");
  const cityPart = [zipCode, city].filter(Boolean).join(" ");
  return [streetPart, cityPart].filter(Boolean).join(", ");
}

export function createEmptyRight(key: string, kind: AssetPropertyRightKind): PropertyRightFormValue {
  return {
    key,
    id: null,
    kind,
    landlord: null,
    isCurrentHome: null,
    propertyDetails: null,
    expectsProceeds: null,
    proceedsEncumbrance: null,
    encumberedClaimId: null,
    encumberedCreditorName: null,
    isProceedsAmountKnown: null,
    proceedsAmount: null,
    proceedsDate: null,
    remarks: null,
    details: null,
  };
}

export function rightIndexesOfKind(rights: PropertyRightFormValue[], kind: AssetPropertyRightKind): number[] {
  return rights.flatMap((right, index) => (right.kind === kind ? [index] : []));
}

export function withEntryAnswered(
  rights: PropertyRightFormValue[],
  kind: AssetPropertyRightKind,
  answer: boolean | null
): PropertyRightFormValue[] {
  if (answer !== true) {
    return rights.filter((right) => right.kind !== kind);
  }
  if (rightIndexesOfKind(rights, kind).length > 0) {
    return rights;
  }
  return [...rights, createEmptyRight(crypto.randomUUID(), kind)];
}

export function withAddedRight(
  rights: PropertyRightFormValue[],
  kind: AssetPropertyRightKind
): PropertyRightFormValue[] {
  const kindIndexes = rightIndexesOfKind(rights, kind);
  const insertAt = (kindIndexes[kindIndexes.length - 1] ?? rights.length - 1) + 1;
  return [...rights.slice(0, insertAt), createEmptyRight(crypto.randomUUID(), kind), ...rights.slice(insertAt)];
}

export function withoutRight(rights: PropertyRightFormValue[], index: number): PropertyRightFormValue[] {
  const removed = rights[index];
  if (!removed) {
    return rights;
  }
  const remaining = rights.filter((_, i) => i !== index);
  if (rightIndexesOfKind(remaining, removed.kind).length > 0) {
    return remaining;
  }
  return [...remaining, createEmptyRight(crypto.randomUUID(), removed.kind)];
}

const EMPTY_ANSWERS: PropertyRightAnswers = {
  hasTenancyRights: null,
  tenantOrOwnerOfHomeAddress: null,
  hasCompanyShares: null,
  hasCooperativeMemberships: null,
  hasOtherPropertyRights: null,
};

const EMPTY_DOCUMENTS = Object.fromEntries(
  PROPERTY_RIGHT_KINDS.map(({ fieldName }) => [fieldName, [] as File[]])
) as Record<PropertyRightDocumentField, File[]>;

export function fromPropertyRightsResponse(data: PropertyRightsTab | undefined): PropertyRightsFormValues {
  return {
    answers: data?.answers ?? EMPTY_ANSWERS,
    propertyRights: (data?.propertyRights ?? []).map((right) => ({
      key: right.id,
      id: right.id,
      kind: right.kind,
      landlord: right.landlord ?? null,
      isCurrentHome: right.isCurrentHome ?? null,
      propertyDetails: right.propertyDetails ?? null,
      expectsProceeds: right.expectsProceeds ?? null,
      proceedsEncumbrance: right.proceedsEncumbrance ?? null,
      encumberedClaimId: right.encumberedClaimId ?? null,
      encumberedCreditorName: right.encumberedCreditorName ?? null,
      isProceedsAmountKnown: right.isProceedsAmountKnown ?? null,
      proceedsAmount: right.proceedsAmount ?? null,
      proceedsDate: right.proceedsDate ?? null,
      remarks: right.remarks ?? null,
      details: right.details ?? null,
    })),
    ...EMPTY_DOCUMENTS,
    documentMoves: [],
  };
}

export function toPropertyRightsRequest(values: PropertyRightsFormValues): SavePropertyRights {
  return {
    answers: values.answers,
    propertyRights: values.propertyRights.map(
      ({ key: _key, encumberedCreditorName: _creditorName, proceedsDate, ...right }) => ({
        ...right,
        proceedsDate: isValidDate(proceedsDate) ? proceedsDate : null,
      })
    ),
  };
}
